/**
 * MediaPickerModal
 *
 * Canonical, fully-controlled image picker. Operates in two modes driven by
 * the `mode` prop:
 *  - "folder" (default) — full folder-hierarchy browser with search, sort, filter.
 *  - "flat"             — filtered list driven by queryParams.
 *
 * Returns MediaAsset (narrow type), never the full DocumentInterface.
 *
 * Usage:
 *   // Pair with useMediaPicker for clean state management:
 *   const { pickerProps, openPicker } = useMediaPicker({ onConfirm: handleAsset })
 *   <button onClick={openPicker}>Pick image</button>
 *   <MediaPickerModal {...pickerProps} title="Choose a reference image" />
 *
 *   // Or control it directly:
 *   <MediaPickerModal
 *     open={open}
 *     onOpenChange={setOpen}
 *     onSelect={asset => { ... }}
 *     selectedAssets={[]}
 *     mode="flat"
 *     queryParams={{ 'metadata.channel_type': 'character', include_children: 'true' }}
 *   />
 */

import { useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useMediaBrowser } from '@/hooks/useMediaBrowser'
import { MediaBrowser } from '@/components/media/MediaBrowser'
import type { MediaAsset } from '@/services/folders/mediaTypes'
import { useSocketContext } from '@/context'

// ── Props ─────────────────────────────────────────────────────────────────────

export interface MediaPickerModalProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    title?: string
    onSelect: (asset: MediaAsset) => void
    multiSelect?: boolean
    selectedAssets?: MediaAsset[]
    /** Allow uploads from within the picker (non-destructive). */
    allowUpload?: boolean
    /**
     * "folder" (default) — full hierarchy browser.
     * "flat"             — flat filtered grid driven by queryParams.
     */
    mode?: 'folder' | 'flat'
    /**
     * flat mode: the query params sent to /documents.
     * folder mode: used as baseQueryParams (merged with search/sort/filter).
     */
    queryParams?: Record<string, string>
}

// ── Main component ─────────────────────────────────────────────────────────────

export function MediaPickerModal({
    open,
    onOpenChange,
    title = 'Media',
    onSelect,
    multiSelect = false,
    selectedAssets = [],
    allowUpload = false,
    mode = 'folder',
    queryParams = {},
}: MediaPickerModalProps) {
    const browser = useMediaBrowser({
        mode,
        // Always restrict to media items (images + folders). Callers can override
        // individual params by passing queryParams — e.g. a flat-mode picker that
        // filters by metadata.visual_run_id still gets the media_only guard.
        baseQueryParams: { media_only: 'true', ...queryParams },
        viewModeStorageKey: 'media-picker-view-mode',
    })

    // Auto-refresh when an AI image generation completes (picker is open)
    const { socket } = useSocketContext()
    useEffect(() => {
        if (!socket || !open) return
        const handler = () => browser.refetch()
        socket.on('image-generated', handler)
        return () => { socket.off('image-generated', handler) }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket, open, browser.refetch])

    // Reset folder nav when modal closes
    useEffect(() => {
        if (!open) {
            // Reset folder history on close so next open starts at root
            // (folderHistory is internal to useMediaBrowser, cleared via re-mount)
        }
    }, [open])

    const handleSelect = (asset: MediaAsset) => {
        onSelect(asset)
        if (!multiSelect) {
            onOpenChange(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='max-w-[820px]'>
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                </DialogHeader>

                <MediaBrowser
                    browser={browser}
                    context='picker'
                    allowUpload={allowUpload}
                    onSelect={handleSelect}
                    selectedAssets={selectedAssets}
                    multiSelect={multiSelect}
                    heightClass='h-[420px]'
                />

                <div className='flex justify-end pt-2 border-t border-gray-100'>
                    <Button variant='outline' onClick={() => onOpenChange(false)}>
                        Close
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    )
}

export default MediaPickerModal
